import { useState, useEffect } from 'react';
import * as Designs from '@pkg/entities/designs';
import * as Library from '@pkg/entities/library';
import makeGraph from '@pkg/entities/snapshots/graph/makeGraph.js';
import mapDesign from '../utils/mapDesign';

export const derive = (library, underived) => {
  const { latest, ...design } = underived;
  const snapshot = makeGraph(latest?.snapshot, { library });

  return {
    snapshot,
    design: {
      ...design,
      latest: {
        ...latest,
        __metrics: snapshot?.__metrics,
        __percentage: snapshot?.__percentage,
        __visibility: snapshot?.__visibility,
      },
    },
  };
};

const useDerivedDesigns = (designIds) => {
  const { hydrated, isHydrating } = Designs.useHydrated(designIds);
  const library = Library.useLibrary();
  const [designs, setDesigns] = useState(new Map());

  useEffect(() => {
    if (isHydrating) {
      return;
    }

    const mapped = new Map();
    let updated = false;

    const main = designs?.get(hydrated.main?.uuid);
    if (main?.design?.latest?.uuid !== hydrated.main?.latest?.uuid) {
      const { snapshot, design } = derive(library, hydrated.main);
      const snapshotEntityMap = mapDesign(design, snapshot);

      mapped.set(design.uuid, {
        design,
        snapshotEntityMap,
        snapshot,
      });
      updated = true;
    } else if (main) {
      // Add the previously derived main design.
      const { design, snapshot, snapshotEntityMap } = main;
      mapped.set(design.uuid, {
        design,
        snapshot,
        snapshotEntityMap,
      });
    }

    hydrated.scenarios.list.forEach((item) => {
      const scenario = designs?.get(item.uuid);

      const collaborators = JSON.stringify(scenario?.design?.collaborators);
      const hydratedCollaborators = JSON.stringify(item?.collaborators);

      if (
        scenario?.design?.latest?.uuid !== item?.latest?.uuid ||
        collaborators !== hydratedCollaborators
      ) {
        const { snapshot, design } = derive(library, item);
        const snapshotEntityMap = mapDesign(design, snapshot);

        mapped.set(design.uuid, {
          design,
          snapshotEntityMap,
          snapshot,
        });

        updated = true;
      } else if (scenario) {
        // Add the previously derived scenario.
        const { design, snapshot, snapshotEntityMap } = scenario;
        mapped.set(design.uuid, {
          design,
          snapshot,
          snapshotEntityMap,
        });
      }
    });

    if (updated) {
      setDesigns(mapped);
    }
  }, [hydrated, isHydrating]);

  const isLoading = !designs?.size;

  return { designs, isLoading };
};

export default useDerivedDesigns;
